import { useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router-dom';

import { Avatar, Box, Card, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';

import { CompanyActionsMenu } from './components';
import AccordionBasicInformation from './components/AccordionBasicInformation';
import AccordionBusinessChallenges from './components/AccordionBusinessChallenges';
import { CompanyInfo } from './components/CompanyInfo';
import SearchBarCompany from './components/SearchBarCompany';
import { useCompanies, useCompany } from './hooks';
import { Company } from './types';
import bgBio from 'assets/images/network/bg_bio.png';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';
import { useUser } from 'global/UserContext';

const BusinessBio = () => {
  const { user } = useUser();
  const theme = useThemeContext();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const location = useLocation();

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const { selectMenuByDocumentId } = useMenuSelection();

  const hasValidCompanyUuid = useMemo(() => {
    return Boolean(selectedCompany?.uuid);
  }, [selectedCompany?.uuid]);

  const {
    companies,
    loading: loadingCompanies,
    error: companiesError,
  } = useCompanies({
    name: searchTerm || undefined,
  });

  // Hook para company específica - apenas quando uuid existe
  const { company: companyDetails, loading: loadingCompanyDetails } = useCompany(
    hasValidCompanyUuid ? selectedCompany!.uuid : null
  );

  // Restaura o contexto do menu quando recebido via location.state
  useEffect(() => {
    if (location.state?.menuContext) {
      const { documentId, path, preserveLevel } = location.state.menuContext;
      console.log('[BusinessBio] Restaurando contexto do menu:', {
        documentId,
        path,
        preserveLevel,
        currentPath: location.pathname,
      });

      if (preserveLevel) {
        selectMenuByDocumentId(documentId, false, undefined, path, true);
      } else {
        selectMenuByDocumentId(documentId, false, undefined, path);
      }
    }
  }, [location, selectMenuByDocumentId]);

  // Efeito para selecionar company baseado no estado da localização
  useEffect(() => {
    if (location.state?.selectedCompany && companies.length) {
      const company = companies.find(c => c.uuid === location.state.selectedCompany);
      setSelectedCompany(company || null);
    }
  }, [companies, location.state]);

  // Initialize selectedCompany with user's company if available
  useEffect(() => {
    // Don't override if there's already a selection
    if (selectedCompany) return;
    // Don't override if location state will handle it
    if (location.state?.selectedCompany) return;

    // If user has a company and companies are loaded, auto-select it
    if (user?.company && companies.length > 0) {
      const userCompany = companies.find(c => c.uuid === user.company?.uuid);
      if (userCompany) {
        setSelectedCompany(userCompany);
      }
    }
  }, [user?.company?.uuid, companies, selectedCompany, location.state?.selectedCompany]);

  return (
    <Box p={0} width="100%">
      <Box p={2} bgcolor="background.paper" borderRadius={2} mb={2}>
        <SearchBarCompany
          companies={companies}
          value={selectedCompany}
          onSelectCompany={(uuid: string | null) => {
            if (uuid) {
              const company = companies.find(c => c.uuid === uuid);
              setSelectedCompany(company || null);
            } else {
              setSelectedCompany(null);
            }
          }}
          loading={loadingCompanies}
        />

        {companiesError && (
          <Typography color="error" sx={{ mt: 1 }}>
            Erro ao buscar companies: {companiesError}
          </Typography>
        )}
      </Box>

      {hasValidCompanyUuid && selectedCompany && (
        <Card
          sx={{
            height: isMobile ? 'auto' : '100%',
            borderRadius: 2,
            backgroundImage: `url(${bgBio})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              bgcolor: theme.background.primary,
              opacity: 0.4,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />

          <Stack
            sx={{ position: 'relative', zIndex: 2 }}
            justifyContent="space-between"
            direction={isMobile ? 'column' : 'row'}
            spacing={isMobile ? 2 : 0}
            width="100%"
          >
            <Stack width="100%" p={1} spacing={2} direction="column" alignItems="center">
              {selectedCompany && (
                <Stack
                  width="100%"
                  direction={isMobile ? 'column' : 'row'}
                  justifyContent="space-between"
                >
                  <Stack
                    direction={isMobile ? 'column' : 'row'}
                    gap={isMobile ? 2 : 4}
                    alignItems={isMobile ? 'flex-start' : 'flex-start'}
                    width="100%"
                    paddingY={1}
                  >
                    <Box
                      alignItems="start"
                      justifyContent="start"
                      display="flex"
                      width={isMobile ? 1 : 'auto'}
                    >
                      <Avatar
                        src={selectedCompany.logo_url ?? undefined}
                        alt={selectedCompany.name}
                        sx={{
                          width: isMobile ? 160 : 240,
                          height: isMobile ? 160 : 240,
                          margin: isMobile ? '0 auto' : 0,
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, height: '100%' }}>
                      <CompanyInfo
                        company={selectedCompany}
                        companyDetails={companyDetails}
                        loadingDetails={loadingCompanyDetails}
                      />
                    </Box>
                  </Stack>

                  <Box
                    width={isMobile ? 1 : 'auto'}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <CompanyActionsMenu
                      loggedUserId={user?.id || ''}
                      selectedUserId={selectedCompany.uuid}
                    />
                  </Box>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Card>
      )}

      {!hasValidCompanyUuid && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="start"
          width="100%"
          height="100%"
          p={2}
        >
          <Typography color="text.secondary" mt={3}>
            Nenhuma empresa selecionada.
          </Typography>
        </Box>
      )}

      <Stack width={1} spacing={isMobile ? 1 : 2} mt={isMobile ? 1 : 2}>
        {hasValidCompanyUuid && selectedCompany && (
          <>
            <AccordionBasicInformation companyDetails={companyDetails} />
            <AccordionBusinessChallenges
              fromUserId={user?.id || ''}
              toUserId={selectedCompany.uuid}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

export default BusinessBio;
